/**
 * Identifies which backend the app is talking to so non-production environments
 * can be surfaced in the UI (e.g. a "DEV" badge in the sidebar).
 *
 * The Momentum API base URL is the source of truth — the exact same value the
 * Kiota client reads in `apiClientFactory.ts`. Production points at
 * `https://api.nymblecommerce.com` (or a same-origin/empty base URL) and shows
 * no badge, whereas dev points at `https://api-dev.nymblecommerce.com` and local
 * builds at localhost. Keying off this variable (rather than the build mode)
 * means the badge always reflects the backend that requests are actually being
 * sent to.
 */

// Mirror `apiClientFactory.ts`: prefer the shell/`define`-injected `process.env`
// value (what the API client uses), falling back to the `.env`-file value.
const apiBaseUrl: string = (
    process.env.VITE_MOMENTUM_API_BASE_URL ||
    import.meta.env.VITE_MOMENTUM_API_BASE_URL ||
    ''
)
    .toString()
    .trim();

// Same two-source read as the API base URL above: CI passes the key through the shell (the workflow
// env block), while a local `dev` run only has it in `.env.development`. The publishable key is safe in
// the bundle by design — it identifies the Stripe account and can only create tokens, never charge.
const stripePublishableKey: string = (
    process.env.VITE_STRIPE_PUBLISHABLE_KEY ||
    import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
    ''
)
    .toString()
    .trim();

// Maps account client id for the admin map control. Not a secret: it only says which account a
// token is for; the token itself comes from the API (`GET /api/maps/token`) at runtime.
const azureMapsClientId: string = (
    process.env.VITE_AZURE_MAPS_CLIENT_ID ||
    import.meta.env.VITE_AZURE_MAPS_CLIENT_ID ||
    ''
)
    .toString()
    .trim();

export type AppEnvironment = 'production' | 'development' | 'local';

function resolveHost(url: string): string {
    try {
        return new URL(url).hostname.toLowerCase();
    } catch {
        return url.toLowerCase();
    }
}

/**
 * Pure classifier (exported for testing): maps an API base URL to an environment
 * and its display label. A `null` label means "no badge".
 */
export function resolveEnvironment(url: string): { environment: AppEnvironment; label: string | null } {
    // No explicit API host => served same-origin => production.
    if (!url) return { environment: 'production', label: null };

    const host = resolveHost(url);

    if (host.includes('localhost') || host.includes('127.0.0.1') || host.startsWith('192.168.')) {
        return { environment: 'local', label: 'LOCAL' };
    }
    // Dev backends carry an explicit `-dev` infix (e.g. api-dev.nymblecommerce.com),
    // which is what distinguishes them from the production host (api.nymblecommerce.com).
    if (host.includes('-dev')) {
        return { environment: 'development', label: 'DEV' };
    }
    // Production (api.nymblecommerce.com) and any other unrecognised host fall through
    // to "no badge" — the indicator must never appear in production.
    return { environment: 'production', label: null };
}

const { environment, label } = resolveEnvironment(apiBaseUrl);
const isProduction = environment === 'production';

/**
 * Returns static environment metadata derived from the API base URL. The values
 * are computed once at module load (env vars don't change at runtime), so they
 * are returned as plain values rather than refs.
 */
export function useEnvironment() {
    return {
        /** The resolved Momentum API base URL ('' when same-origin/production). */
        apiBaseUrl,
        /** Coarse environment classification. */
        environment,
        /** Short uppercase label for display ('DEV', 'LOCAL', …) or null in production. */
        environmentLabel: label,
        /** True when talking to the production backend. */
        isProduction,
        /** True for any non-production backend (dev or local). */
        isNonProduction: !isProduction,
        /** Stripe publishable key for this build ('' when payments aren't configured). */
        stripePublishableKey,
        /** Maps account client id for the admin map control ('' when maps aren't configured). */
        azureMapsClientId,
    };
}
